import { useDropzone } from "react-dropzone";
import type { ImageFile } from "../types.ts";
import { loadImage } from "../utils/image.ts";
import { notifier } from "../utils/notifier.tsx";
import { pseudoRandomUUID } from "../utils/stackoverflow.ts";

export const useImageDropZone = ({
  multiple,
  minImageSize,
  onImageValidated,
  onImagesValidated,
  maxFiles,
  acceptSvg,
}: {
  multiple: boolean;
  minImageSize: number;
  onImageValidated?: (imageFile: ImageFile | undefined) => void;
  onImagesValidated?: (imageFiles: ImageFile[]) => void;
  maxFiles?: number;
  acceptSvg?: boolean;
}) => {
  const dropzone = useDropzone({
    multiple,
    noClick: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/webp": [".webp"],
      ...(acceptSvg ? { "image/svg+xml": [".svg"] } : {}),
    },
    onDrop: (acceptedFiles) => {
      void (async () => {
        if (multiple && maxFiles && acceptedFiles.length > maxFiles) {
          notifier.error(
            `You can upload a maximum of ${maxFiles} images at once`,
          );
          return;
        }

        const validFiles: ImageFile[] = [];

        for (const acceptedFile of acceptedFiles) {
          try {
            const image = await loadImage(URL.createObjectURL(acceptedFile));
            if (image.width < minImageSize || image.height < minImageSize) {
              notifier.error(
                `Your image dimensions should be greater than ${minImageSize}x${minImageSize} pixels`,
              );
            } else {
              const fileData = {
                uuid: pseudoRandomUUID(),
                fileToUpload: acceptedFile,
                width: image.width,
                height: image.height,
              };
              validFiles.push(fileData);

              if (onImageValidated) onImageValidated(fileData);
            }
          } catch {
            notifier.error("Failed to load image");
          }
        }
        if (onImagesValidated && validFiles.length > 0) {
          onImagesValidated(validFiles);
        }
      })();
    },
  });

  return {
    ...dropzone,
    maxFiles,
  };
};
